/**
 * This base configuration is extended per maison and can be referenced by
 * components within the app.
 */

import freeze from "deep-freeze-strict";
import { AppConfig } from "../types";
import { get } from "../utils/helpers";
import { fromDDCCustomer, toDDCCustomer } from "./shared/transforms";

import customerDataForm from "./shared/forms/customerData";
import customerConsentForm from "./shared/forms/customerConsent";
import customerAdditionalInfo from "./shared/forms/customerAdditionalInfo";
import advisorForm from "./shared/forms/advisorForm";
import debug from "../utils/debug";

const additionalInfoCountries = [
  "JP",
  "CN",
  "HK",
  "MO",
  "TW",
  "SG",
  "KR",
  "JPN"
];
const additionalInfoLanguages = ["ja", "zh", "zh-Hans", "zh-Hant", "ko"];

const stage: string = process.env.REACT_APP_STAGE || "dev";
const BACKEND_BASE_URL: string =
  stage === "dev"
    ? "https://core-api.staging.k8s.clienteling.lvmhda.com/v1"
    : stage === "staging"
    ? "https://core-api.uat.k8s.clienteling.lvmhda.com/v1"
    : "https://core-api.prd.clienteling.lvmhda.com/v1";
debug("env:", process.env.NODE_ENV, process.env.REACT_APP_STAGE);
debug("baseUrl:", BACKEND_BASE_URL);

export const DEFAULT_CONFIG: AppConfig = {
  id: "lvmh",
  name: "Digital Card",
  languages: {
    supportedLanguages: [
      "en",
      "es",
      "fr",
      "it",
      "de",
      "nl",
      "cs",
      "sv",
      "ar",
      "ja",
      "zh-Hant",
      "zh-Hans"
    ],
    // These settings control the `enterInRoman` property on the Field label
    // as well as showing the additional info form after a customer has
    // entered in their data.
    nonRomanLanguages: [
      "ja",
      "zh",
      "zh-Hant",
      "zh-Hans",
      "ko",
      "th",
      "hi",
      "ru",
      "uk"
    ],
    loadPath: "/i18n/{{lng}}/{{ns}}"
  },
  notifications: {
    localStorageKey: "notifications",
    timeExpires: 1000 * 60 * 60 * 24 * 7,
    showPerPage: 20
  },
  errorCodes: {
    APP: 99,
    NETWORK: 1,
    SERVER: 2,
    DATA_CONNECTION: 3,
    DATA_ERROR: 4
  },
  customise: {
    header: {
      showSetup: true,
      showLanguage: true,
      showNotifications: true,
      showLogout: true
    },
    footer: {
      showLanguage: false
    },
    languageSwitcher: {
      showOnlyZoneLanguages: false
    },
    notifications: {
      enableDisplayCustomer: false
    },
    verifySalesPerson: {
      enable: true,
      capitaliseId: true
    },
    searchCustomer: {
      enable: false,
      resultsFields: [],
      detailsColumns: [],
      enableEdit: false
    },
    customer: {
      enableAdditionalInfo: true,
      checkShowAdditionalInfo: (sharedState: {
        appState?: object;
        pageState?: object;
        formState?: object;
      }): boolean => {
        const appStoreCountry = get(sharedState, "appState.storeCountry");
        const customerLanguage = get(sharedState, "pageState.language");
        const showAdditionalInfo =
          appStoreCountry &&
          additionalInfoCountries.includes(appStoreCountry) &&
          customerLanguage &&
          additionalInfoLanguages.includes(customerLanguage);
        return showAdditionalInfo;
      }
    },
    fields: {
      showRequiredLabel: true,
      showOptionalLabel: false,
      markRequiredWithSymbol: false
    },
    setupPage: {
      filterStoresByZone: false
    }
  },
  /**
   * Data transforms.
   */
  transforms: {
    customer: {
      read: fromDDCCustomer,
      write: toDDCCustomer
    }
  },
  /**
   * Form definitions.
   */
  forms: {
    schema: "form/customerFormDefinitions.json",
    fields: [
      "form/customerDataFields.json",
      "form/customerConsentFields.json",
      "form/customerThanks.json",
      "form/additionalInfoFields.json"
    ],
    customer: [
      customerDataForm,
      customerConsentForm,
      customerAdditionalInfo,
      advisorForm
    ]
  },
  services: {
    api: {
      baseUrl: BACKEND_BASE_URL,
      loginRedirectEndpoint: "/auth/login",
      logoutEndpoint: "/auth/logout",
      getTokenEndpoint: "/auth/token",
      refreshTokenEndpoint: "/auth/refresh",
      getConfigEndpoint: "/ddcconfig",
      verifyAdvisorEndpoint: "/advisors",
      getTitlesEndpoint: "/titles",
      getCountriesEndpoint: "/countries",
      getStoresEndpoint: "/stores",
      translationsEndpoint: "/translations/{{lng}}/{{ns}}?app=ddc",
      customerEndpoint: "/clients",
      encodeSignature: true
    },
    googleAnalytics: {
      options: {},
      customise: {
        dimensions: {
          traceToken: "dimension1",
          salesPersonId: "dimension2",
          storeId: "dimension3",
          storeCountry: "dimension4",
          attempt: "dimension5",
          requestId: "dimension6",
          storeName: "dimension7",
          countryName: "dimension8"
        },
        metrics: {}
      }
    }
  }
} as any;

export default freeze(DEFAULT_CONFIG);
